import { useEffect, useRef, useState } from "react";
import {
  AdjustWrapper,
  CenterDot,
  Container,
  DialCircle,
  DialContainer,
  DialLine,
  DialPin,
  DoubleCol,
  HelpingIcon,
  InputWrapper,
  MobileLeftWrapper,
  NumberInput,
  ResetButton,
  RotationIcon,
  RotationWheelText,
  RotationWheelWrapper,
} from "./styled";
import { getIcon } from "../../utils/get-icon";
import { Tooltip } from "react-tooltip";

interface RotationDialProps {
  value: number;
  onChange: (rotation: number) => void;
  isMobile?: boolean;
}

const RotationDial = ({
  value,
  onChange,
  isMobile = false,
}: RotationDialProps) => {
  const [rotation, setRotation] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const dialRef = useRef(null);
  const inputRef = useRef(null);

  const calculatePinPosition = (angle: number) => {
    const radians = (angle - 90) * (Math.PI / 180);

    // Adjust radius based on mobile view
    const radius = isMobile ? 27 : 16;
    const x = radius * Math.cos(radians);
    const y = radius * Math.sin(radians);

    return { x, y };
  };

  const pinPosition = calculatePinPosition(rotation);

  const startDrag = (e: any) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDrag = (e: any) => {
    if (!isDragging || !dialRef.current) return;

    const dialElement = dialRef.current as HTMLDivElement;
    const rect = dialElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (clientX === undefined || clientY === undefined) return;

    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;
    let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;

    if (angle < 0) angle += 360;

    setRotation(Math.round(angle));
    onChange(rotation);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const endDrag = () => {
    setIsDragging(false);
    onChange(rotation);
  };

  const handleInputChange = (e: any) => {
    let value = parseInt(e.target.value, 10);

    if (isNaN(value)) value = 0;

    value = ((value % 360) + 360) % 360;

    setRotation(value);
    onChange(value);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDrag);
      window.addEventListener("touchmove", handleDrag);
      window.addEventListener("mouseup", endDrag);
      window.addEventListener("touchend", endDrag);
    }

    return () => {
      window.removeEventListener("mousemove", handleDrag);
      window.removeEventListener("touchmove", handleDrag);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchend", endDrag);
    };
  }, [endDrag, handleDrag, isDragging]);

  return isMobile ? (
    <Container isMobile={isMobile}>
      <MobileLeftWrapper>
        <InputWrapper>
          <RotationIcon>{getIcon("rotation")}</RotationIcon>
          <NumberInput
            ref={inputRef}
            type="number"
            value={rotation}
            onChange={handleInputChange}
          />
        </InputWrapper>
        <ResetButton>{getIcon("reset")}</ResetButton>
      </MobileLeftWrapper>
      <RotationWheelWrapper>
        <RotationWheelText>
          Trykk og dra hjulet rundt for å rotere.
        </RotationWheelText>
        <AdjustWrapper>
          <DialContainer
            isMobile={isMobile}
            ref={dialRef}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
          >
            <DialCircle />
            <CenterDot isMobile={isMobile} />
            <DialLine rotation={rotation} isMobile={isMobile} />
            <DialPin x={pinPosition.x} y={pinPosition.y} isMobile={isMobile} />
          </DialContainer>
        </AdjustWrapper>
      </RotationWheelWrapper>
    </Container>
  ) : (
    <Container isMobile={isMobile}>
      <DoubleCol>
        <InputWrapper>
          <RotationIcon>{getIcon("rotation")}</RotationIcon>
          <NumberInput
            ref={inputRef}
            type="number"
            value={rotation}
            onChange={handleInputChange}
          />
        </InputWrapper>

        <AdjustWrapper>
          <DialContainer
            ref={dialRef}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            isMobile={isMobile}
          >
            <DialCircle />
            <CenterDot isMobile={isMobile} />
            <DialLine isMobile={isMobile} rotation={rotation} />
            <DialPin isMobile={isMobile} x={pinPosition.x} y={pinPosition.y} />
          </DialContainer>
          <HelpingIcon
            data-tooltip-id="rotationBtnTooltip"
            data-tooltip-content="Trykk og juster med musepekeren for å rotere valgt element."
          >
            {getIcon("information")}
          </HelpingIcon>

          <Tooltip id="rotationBtnTooltip" />
        </AdjustWrapper>
      </DoubleCol>
    </Container>
  );
};

export default RotationDial;
